import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { DynamicActionTemplateMessages } from "src/app/shared/components/dynamic-action-template/dynamic-action-template-messages";
import { DynamicFormComponent } from "src/app/shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "src/app/shared/components/dynamic-form/question-base";
import { DateQuestion } from "src/app/shared/components/dynamic-form/question-date";
import { NotificationService } from "src/app/shared/components/notification/notification.service";
import {
  SimpleDialogComponent,
  SimpleDialogProperties,
} from "src/app/shared/components/simple-dialog/simple-dialog.component";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { QueryParams } from "../../../core/http/query-params.type";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  DataTableParamsWhere,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { GenericModalComponent } from "../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../shared/components/generic-modal/generic-modal.type";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { FORMAT } from "../../../shared/constants/formats";
import { VALUE } from "../../../shared/constants/values";
import { MomentPipe } from "../../../shared/pipes/moment/moment.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { formatPossession } from "../../../shared/utils/text-formatter";
import { isBetweenValidator } from "../../../shared/validators/is-between.validator";
import { AssetTableService } from "../assets/asset-table.service";
import { Asset } from "../assets/asset.type";
import { CompanyService } from "../companies/company.service";
import { MaintenanceProviderService } from "../maintenance-provider/maintenance-provider.service";
import { PartnerService } from "../partners/partner.service";
import { contractStatuses, contractTypes, getContractSearchFilterOptions } from "./contract";
import { ContractService } from "./contract.service";
import { Contract, ContractStatus } from "./contract.type";
import { RenewContractComponent, RenewContractComponentProps } from "./renew-contract/renew-contract.component";

@Injectable({
  providedIn: "root",
})
export class ContractTableService {
  currentUser: UserInfo;
  dialogRef: MatDialogRef<GenericModalComponent>;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private contractService: ContractService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private maintenanceProviderService: MaintenanceProviderService,
    private assetTableService: AssetTableService,
    private dataTableService: DataTableService<Contract>,
    private notificationService: NotificationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getContractsTableConfig = (config?: DataTableConfig): DataTable<Contract> => {
    const { hidden, shown, actionColumn, inlineEdit, entities } = config || {};

    const columns: DataTableColumn<Contract>[] = (
      [
        {
          prop: "name",
          name: "Name",
          inlineEdit: {
            control: "input",
            disabled: (contract) => inlineEdit?.disabled?.(contract),
            validators: () => [Validators.minLength(2), Validators.maxLength(255)],
          },
        },
        {
          prop: "number",
          name: "Contract #",
          render: (contract) => contract.numberVersion,
          link: {
            path: (contract) => `/contracts/${contract.id}`,
          },
          exportCsv: {
            csvMapperKey: "numberVersion",
          },
        },
        {
          prop: "company.partner.name",
          name: "Partner",
          hidden: !this.currentUser.isGlobal(),
          link: {
            path: (contract) => `/partners/${contract.company.partnerId}`,
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (contract) => `/companies/${contract.companyId}`,
            disabled: () => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
        {
          prop: "reseller",
          name: "Reseller",
          hidden:
            this.currentUser.isCompanyOrViewAsCompany() ||
            (this.currentUser.isPartnerOrViewAsPartner() && !this.currentUser?.partner?.isResellerNeeded),
          inlineEdit: {
            control: "input",
            disabled: (contract) => !contract.company?.partner?.isResellerNeeded || inlineEdit?.disabled?.(contract),
            required: false,
            validators: () => [Validators.minLength(0), Validators.maxLength(100)],
          },
        },
        {
          prop: "startDate",
          name: "Start Date",
          inlineEdit: {
            control: "date",
            validators: (contract) => [isBetweenValidator(VALUE.DEFAULT_MIN_DATE, contract.endDate, "[]")],
            date: {
              min: () => moment(VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (contract) => moment(contract.endDate).toDate(),
            },
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "endDate",
          name: "End Date",
          inlineEdit: {
            control: "date",
            validators: (contract) => [isBetweenValidator(contract.startDate, VALUE.DEFAULT_MAX_DATE, "[]")],
            disabled: (contract) => inlineEdit?.disabled?.(contract),
            date: {
              min: (contract) => moment(contract.startDate).toDate(),
              max: () => moment(VALUE.DEFAULT_MAX_DATE).toDate(),
            },
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "status",
          name: "Status",
          render: (contract) => contractStatuses.find((status) => status.value === contract.status)?.label,
        },
        {
          prop: "type",
          name: "Type",
          render: (contract) => contractTypes.find((type) => type.value === contract.type)?.label,
          inlineEdit: {
            disabled: (contract) => inlineEdit?.disabled?.(contract),
            control: "select",
            select: {
              options: [...contractTypes],
            },
          },
        },
        {
          prop: "maintenanceProvider.name",
          name: "Maintenance Provider",
          width: 200,
          hidden: this.currentUser.isCompanyOrViewAsCompany(),
          inlineEdit: {
            disabled: (contract) => inlineEdit?.disabled?.(contract),
            control: "search",
            search: {
              columnToUpdate: "maintenanceProviderId",
              propToUpdate: "maintenanceProvider",
              propToDisplay: "name",
              fetchDefaultData: true,
              service: (formValue: string) =>
                this.maintenanceProviderService.getMaintenanceProviders({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                  },
                }),
            },
          },
        },
      ] as DataTableColumn<Contract>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (contract) => col?.inlineEdit?.disabled?.(contract) || !this.canEdit(this.currentUser, contract),
        },
      }));

    return {
      id: "contracts",
      entityName: "Contract",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "contracts",
          data: {
            browser: (queryParams) =>
              this.contractService.getContracts({
                ...queryParams,
                where: {
                  companyId: entities?.company?.id,
                  ...removePropsWithKeys<Contract>(queryParams.where || {}, ["partner.name", "company.name"]),
                },
                select: { ...queryParams.select, version: true },
              }),
            email: (queryParams) =>
              this.contractService.getContractsQueue({
                ...queryParams,
                where: {
                  companyId: entities?.company?.id,
                  ...removePropsWithKeys<Contract>(queryParams.where || {}, ["partner.name", "company.name"]),
                },
                select: { ...queryParams.select, version: true },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewContract(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "table_rows",
              text: "View Assets",
              function: this.viewAssets,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageContract,
              hidden: () => actionColumn?.view?.hidden,
            },
            {
              icon: "update",
              text: "Contract Renewal",
              function: this.renewContract,
              hidden: () => !this.canRenew(this.currentUser),
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteContract,
              hidden: (contract) => actionColumn?.delete?.hidden || !this.canDelete(contract, this.currentUser),
            },
            {
              icon: "cancel",
              text: "Terminate Contract",
              function: this.terminateContract,
              hidden: (contract) => actionColumn?.unterminate?.hidden || !this.canTerminate(contract, this.currentUser),
            },
            {
              icon: "cancel",
              text: "Unterminate Contract",
              function: this.unterminateContract,
              hidden: (contract) => actionColumn?.terminate?.hidden || !this.canUnterminate(contract, this.currentUser),
            },
            {
              icon: "picture_as_pdf",
              text: "Welcome Letter Generator",
              function: this.getWelcomeLetter,
              hidden: () => !this.currentUser.isGlobal(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getContractSearchFilterOptions(this.currentUser, {
            hidden,
            shown,
            partnerService: this.partnerService,
            companyService: this.companyService,
          }),
        },
      ],
    };
  };

  /** Populate table */
  getContracts = (event: GetDataEvent<Contract>, assetId?: string): void => {
    const { onError, onResult, queryParams } = event;
    const params: QueryParams<Contract> = {
      ...queryParams,
      relations: { maintenanceProvider: true, company: { partner: true } },
      where: {
        ...removePropsWithKeys<Contract>(queryParams.where, ["partner.name", "company.name"]),
        ...(assetId ? { contractAssets: { assetId } } : {}),
      },
    };

    this.contractService.getContracts(params).subscribe(
      (data) => {
        onResult(data);
      },
      (error) => {
        onError(error);
      },
    );
  };

  /** Update table */
  update = (event: UpdateDataEvent<Contract>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      // If startDate is updated then we have to pass its endDate too
      // else if endDate is updated then we have to pass its startDate too
      if (updatedValues.startDate) {
        updatedValues.endDate = row.endDate;
      } else if (updatedValues.endDate) {
        updatedValues.startDate = row.startDate;
      }
      this.contractService.updateContract(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewContract = (contract: Contract, config?: DataTableConfig): void => {
    const columns = this.getContractsTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(contract, col, get(contract, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private viewAssets = (contract: Contract): void => {
    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Asset> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: formatPossession(contract.name, "Assets"),
        buttons: { hide: true },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.assetTableService.getAssetsTableConfig({
              entities: { contract },
              syncWithQueryParams: false,
              hidden: ["partner.name", "dmr", "monitoring", "fairMarketValue", "bookValue"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Asset>) => {
                return this.assetTableService.getAssets({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: { ...event.queryParams.where, contractId: contract.id } as DataTableParamsWhere<Asset>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private manageContract = (contract: Contract): void => {
    this.router.navigate([`/contracts/${contract.id}`]);
  };

  /** Check if the selected contract is the latest version or not, and depending on it shows the desired modal */
  private renewContract = (contract: Contract, table: DataTableComponent<Contract>): void => {
    this.contractService
      .getContracts({
        where: { number: contract.number },
        relations: { maintenanceProvider: true, company: { partner: true } },
        order: { version: "desc" },
        pagination: { page: 1, pageSize: 1 },
      })
      .subscribe((response) => {
        const latestContract = response.items?.[0];
        if (latestContract.status === ContractStatus.pending) {
          this.showContractIsPending(latestContract);
        } else if (latestContract.id === contract.id) {
          this.showContractToRenew(contract, table);
        } else {
          this.showContractRedirect(latestContract, table);
        }
      });
  };

  private showContractIsPending = (contract: Contract): void => {
    const title = `Unable to renew contract: contract ${contract.numberVersion} is already pending for renewal`;

    this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title,
        component: null,
        buttons: {
          submit: { hide: true },
          cancel: { name: "Close" },
        },
      },
    });
  };

  private showContractToRenew = (contract: Contract, table: DataTableComponent<Contract>): void => {
    const modal = this.dialog.open<RenewContractComponent, RenewContractComponentProps>(RenewContractComponent, {
      id: "renewalContract",
      width: "1400px",
      height: "90%",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        contract,
        table,
      },
    });

    modal.beforeClosed().subscribe(() => {
      table.config.rows = table.config.rows.map((_contract) =>
        _contract.id === modal.componentInstance.contract.id ? modal.componentInstance.contract : _contract,
      );
    });
  };

  private showContractRedirect = (contract: Contract, table: DataTableComponent<Contract>): void => {
    const title =
      "The selected contract isn't the latest contract version. Would you like to renew the latest contract version?";
    const action = () => this.showContractToRenew(contract, table);

    this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title,
        buttons: {
          submit: {
            name: "Yes",
            disabled: () => false,
            action,
          },
          cancel: {
            name: "No",
            disabled: () => false,
          },
        },
      },
    });
  };

  private deleteContract = (contract: Contract, table: DataTableComponent<Contract>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "contract",
        data: contract,
        url: this.contractService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private terminateContract = (contract: Contract, table: DataTableComponent<Contract>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.generateQuestionsTerminateContract(contract).subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: DynamicActionTemplateMessages.terminateContract,
        buttons: { hide: true },
        component: {
          clazz: DynamicFormComponent,
          inputs: {
            questions,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: any) => {
                this.contractService.terminateContract(contract.id, values).subscribe(
                  () => {
                    this.dialogRef.close(true);
                    this.notificationService.success(DynamicActionTemplateMessages.contractUpdated);
                    table.search();
                  },
                  (err) => {
                    this.notificationService.error(err);
                  },
                );
              },
            },
          ],
        },
      },
    });
  };

  private unterminateContract = (contract: Contract, table: DataTableComponent<Contract>): void => {
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "confirmUser",
      panelClass: "custom-dialog-container",
      data: {
        title: DynamicActionTemplateMessages.unterminateContract,
        message: "Do you want to confirm this action?",
        type: "yesNo",
        action: "accept",
      },
    });

    dialog.afterClosed().subscribe(async (result) => {
      if (result) {
        this.contractService.unterminateContract(contract.id).subscribe(
          () => {
            dialog.close(true);
            this.notificationService.success(DynamicActionTemplateMessages.contractUpdated);
            table.search();
          },
          (err) => {
            this.notificationService.error(err);
          },
        );
      }
    });
  };

  private generateQuestionsTerminateContract(contract: Contract): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new DateQuestion({
        key: "endDate",
        label: "End Date",
        minDate: moment(contract?.startDate).toDate(),
        maxDate: moment(contract?.endDate).toDate(),
        required: true,
        disabled: false,
        order: 1,
      }),
    ];
    return of(questions.filter((fields) => fields));
  }

  /** Check if can edit a contract (on the table) */
  private canEdit = (currentUser: UserInfo, contract: Contract): boolean => {
    if (currentUser.isGlobal() || (!currentUser.isGlobal() && !contract?.maintenanceProvider?.isChannelWorks))
      return allowUpdateEntity(contract, currentUser);
    return false;
  };

  /** Check if can renew a contract (on the table) */
  private canRenew = (currentUser: UserInfo): boolean => {
    return currentUser.isGlobal() && currentUser.hasRight(["admin", "manager"]);
  };

  /** Check if can delete a contract (on the table) */
  private canDelete = (contract: Contract, currentUser: UserInfo): boolean => {
    // Only users with admin rights can delete
    // As a company or partner cannot be deleted if it is associated with a channelwork contract

    if (currentUser.isGlobal() || (!currentUser.isGlobal() && !contract?.maintenanceProvider?.isChannelWorks))
      return allowDeleteEntity(contract, currentUser);
    return false;
  };

  private canTerminate(contract: Contract, currentUser: UserInfo): boolean {
    return this.canEdit(currentUser, contract) && contract.status !== ContractStatus.terminated;
  }

  private canUnterminate(contract: Contract, currentUser: UserInfo): boolean {
    return this.canEdit(currentUser, contract) && contract.status === ContractStatus.terminated;
  }

  private getWelcomeLetter = async (contract: Contract): Promise<void> => {
    this.contractService.getWelcomeLetter(contract.id).subscribe((response: any) => {
      this.savePdfFile(response, contract);
    });
  };

  private savePdfFile(blob: Blob, contract: Contract) {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `Welcome-Template-${contract.name}.pdf`;
    link.click();
  }

  //#endregion
}
